/**
 * `sessionStorage`, made safe to call anywhere.
 *
 * Per tab and gone when the tab closes — the right lifetime for a form in
 * progress, and the one OWASP recommends over `localStorage` for anything not
 * meant to persist. Every access can throw (private windows, blocked site
 * data, a full quota), so each one is caught: a failed read is "nothing
 * stored" and a failed write is skipped, and callers keep working from memory.
 */

export function readSession<T>(name: string): T | null {
  try {
    const raw = sessionStorage.getItem(name);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

export function writeSession(name: string, value: unknown): void {
  try {
    sessionStorage.setItem(name, JSON.stringify(value));
  } catch {
    // Storage unavailable: the caller's in-memory copy still holds.
  }
}

export function removeSession(name: string): void {
  try {
    sessionStorage.removeItem(name);
  } catch {
    // Nothing to clean up if storage was never reachable.
  }
}
